import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import {
  Building2,
  Upload,
  Palette,
  Check,
  Loader2,
  Globe,
  Mail,
  Phone,
  MapPin,
  FileText
} from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/use-toast";
import { companyAPI } from "@/services/companyService";
import { useDispatch, useSelector } from "react-redux";
import { updateCompany } from "@/store/slices/authSlice";
import { setBranding, fetchBranding } from "@/store/slices/brandingSlice";
import { RootState, AppDispatch } from "@/store";

export default function OrganizationSettings() {
  const { company } = useAuth();
  const branding = useSelector((state: RootState) => state.branding);
  const { toast } = useToast();
  const dispatch = useDispatch<AppDispatch>();

  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    nombre_legal: "",
    email: "",
    phone: "",
    website: "",
    address: "",
    primary_color: "#3b82f6",
    secondary_color: "#64748b",
    logo_url: ""
  });

  useEffect(() => {
    if (branding.status === 'idle') {
      dispatch(fetchBranding());
    }
    
    if (!isInitialized && (branding.status === 'succeeded' || branding.lastUpdated)) {
      setFormData({
        name: branding.name || "",
        nombre_legal: branding.nombre_legal || "",
        email: branding.email || "",
        phone: branding.phone || "",
        website: branding.website || "",
        address: branding.address || "",
        primary_color: branding.primary_color || "#3b82f6",
        secondary_color: branding.secondary_color || "#64748b",
        logo_url: branding.logo_url || ""
      });
      setIsInitialized(true);
    }
  }, [branding, dispatch, isInitialized]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleColorChange = (name: string, value: string) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    try {
      // 1. Obtener URL de subida
      const response = await companyAPI.getLogoUploadUrl(file.name, file.type);
      if (response.success) {
        const { uploadUrl, fileUrl } = response.data;
        
        // 2. Subir a S3
        await companyAPI.uploadToS3(uploadUrl, file);
        
        // 3. Actualizar estado local
        setFormData(prev => ({ ...prev, logo_url: fileUrl }));
        
        toast({
          title: "Logo subido",
          description: "El logo se ha cargado correctamente. No olvides guardar los cambios.",
        });
      }
    } catch (error: any) {
      toast({
        title: "Error al subir logo",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await companyAPI.updateSettings({
        name: formData.name,
        nombre_legal: formData.nombre_legal,
        email: formData.email,
        phone: formData.phone,
        address: formData.address,
        primary_color: formData.primary_color,
        secondary_color: formData.secondary_color,
        logo_url: formData.logo_url
      });

      if (response.success) {
        // Actualizar Auth (para datos generales persistidos en login)
        dispatch(updateCompany(response.data));
        
        // Actualizar Branding (Single Source of Truth para UI y documentos)
        dispatch(setBranding({
          name: response.data.name,
          nombre_legal: response.data.nombre_legal,
          email: response.data.email,
          phone: response.data.phone,
          website: response.data.website,
          address: response.data.address,
          logo_url: response.data.logo_url,
          primary_color: response.data.primary_color,
          secondary_color: response.data.secondary_color,
        }));
        
        toast({
          title: "Configuración guardada",
          description: "Los cambios se han aplicado correctamente y el branding se ha actualizado.",
        });
      }
    } catch (error: any) {
      toast({
        title: "Error al guardar",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50/50 p-6">
      <div className="max-w-4xl mx-auto space-y-8">
        <div className="flex justify-between items-end">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Mi Organización</h1>
            <p className="text-slate-500 mt-2">
              Personaliza la identidad visual y datos legales de tu empresa
            </p>
          </div>
          <Button 
            onClick={handleSubmit} 
            disabled={loading}
            className="bg-blue-600 hover:bg-blue-700 shadow-lg shadow-blue-600/20 px-8"
          >
            {loading ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <Check className="h-4 w-4 mr-2" />
            )}
            Guardar Cambios
          </Button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          <div className="md:col-span-2 space-y-6">
            {/* Información General */}
            <Card className="border-slate-200 shadow-sm overflow-hidden">
              <CardHeader className="bg-white border-b border-slate-100 pb-4">
                <CardTitle className="flex items-center text-lg font-semibold text-slate-900">
                  <div className="p-2 bg-blue-50 text-blue-600 rounded-lg mr-3">
                    <Building2 className="h-5 w-5" />
                  </div>
                  Información Legal
                </CardTitle>
                <CardDescription>
                  Datos que aparecerán en facturas y documentos oficiales
                </CardDescription>
              </CardHeader>
              <CardContent className="pt-6 space-y-4">
                <div className="grid grid-cols-1 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="name">Nombre Comercial</Label>
                    <div className="relative">
                        <Input 
                            id="name" 
                            name="name" 
                            value={formData.name} 
                            onChange={handleInputChange}
                            placeholder="Ej: Prographic"
                            className="pl-10"
                        />
                        <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="nombre_legal">Nombre Legal / Razón Social</Label>
                    <div className="relative">
                        <Input 
                            id="nombre_legal" 
                            name="nombre_legal" 
                            value={formData.nombre_legal} 
                            onChange={handleInputChange}
                            placeholder="Ej: Prographic S.A. de C.V."
                            className="pl-10"
                        />
                        <FileText className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                        <Label htmlFor="email">Correo Electrónico</Label>
                        <div className="relative">
                            <Input 
                                id="email" 
                                name="email" 
                                value={formData.email} 
                                onChange={handleInputChange}
                                placeholder="Ej: contacto@prographic.com"
                                className="pl-10" 
                            />
                            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                        </div>
                    </div>
                    <div className="space-y-2">
                        <Label htmlFor="phone">Teléfono de Contacto</Label>
                        <div className="relative">
                            <Input 
                                id="phone" 
                                name="phone" 
                                value={formData.phone} 
                                onChange={handleInputChange}
                                placeholder="Ej: +503 2222-2222"
                                className="pl-10" 
                            />
                            <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                        </div>
                    </div>
                </div>

                <div className="space-y-2">
                    <Label htmlFor="address">Dirección Física</Label>
                    <div className="relative">
                        <Input 
                            id="address" 
                            name="address" 
                            value={formData.address} 
                            onChange={handleInputChange}
                            placeholder="Ej: Calle Principal #123, San Salvador"
                            className="pl-10" 
                        />
                        <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    </div>
                </div>
              </CardContent>
            </Card>

            {/* Branding / Identidad Visual */}
            <Card className="border-slate-200 shadow-sm">
              <CardHeader className="border-b border-slate-100 pb-4">
                <CardTitle className="flex items-center text-lg font-semibold text-slate-900">
                  <div className="p-2 bg-purple-50 text-purple-600 rounded-lg mr-3">
                    <Palette className="h-5 w-5" />
                  </div>
                  Identidad Visual
                </CardTitle>
                <CardDescription>
                  Personaliza los colores de la interfaz y tu logotipo
                </CardDescription>
              </CardHeader>
              <CardContent className="pt-6 space-y-8">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <div className="space-y-4">
                    <Label className="text-sm font-semibold">Colores Corporativos</Label>
                    
                    <div className="space-y-4">
                      <div className="flex items-center justify-between p-3 bg-white border border-slate-200 rounded-xl">
                        <div className="flex items-center gap-3">
                          <div 
                            className="w-10 h-10 rounded-lg shadow-inner border border-black/5"
                            style={{ backgroundColor: formData.primary_color }}
                          />
                          <div>
                            <p className="text-sm font-medium">Color Primario</p>
                            <p className="text-xs text-slate-400">{formData.primary_color}</p>
                          </div>
                        </div>
                        <Input 
                          type="color" 
                          value={formData.primary_color} 
                          onChange={(e) => handleColorChange('primary_color', e.target.value)}
                          className="w-12 h-12 p-1 border-none bg-transparent cursor-pointer"
                        />
                      </div>

                      <div className="flex items-center justify-between p-3 bg-white border border-slate-200 rounded-xl">
                        <div className="flex items-center gap-3">
                          <div 
                            className="w-10 h-10 rounded-lg shadow-inner border border-black/5"
                            style={{ backgroundColor: formData.secondary_color }}
                          />
                          <div>
                            <p className="text-sm font-medium">Color Secundario</p>
                            <p className="text-xs text-slate-400">{formData.secondary_color}</p>
                          </div>
                        </div>
                        <Input 
                          type="color" 
                          value={formData.secondary_color} 
                          onChange={(e) => handleColorChange('secondary_color', e.target.value)}
                          className="w-12 h-12 p-1 border-none bg-transparent cursor-pointer"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <Label className="text-sm font-semibold">Logotipo de la Empresa</Label>
                    <div className="relative group">
                      <div className="aspect-square w-full max-w-[200px] mx-auto bg-white border-2 border-dashed border-slate-200 rounded-2xl flex flex-col items-center justify-center overflow-hidden transition-all group-hover:border-blue-400">
                        {formData.logo_url ? (
                          <>
                            <img src={formData.logo_url} alt="Logo preview" className="w-full h-full object-contain p-4" />
                            <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                                <Button variant="secondary" size="sm" className="bg-white/90 hover:bg-white" asChild>
                                    <label className="cursor-pointer">
                                        <Upload className="h-4 w-4 mr-2" />
                                        Cambiar
                                        <input type="file" className="hidden" accept="image/*" onChange={handleLogoUpload} />
                                    </label>
                                </Button>
                            </div>
                          </>
                        ) : (
                          <label className="flex flex-col items-center justify-center cursor-pointer w-full h-full">
                            {uploading ? (
                                <Loader2 className="h-8 w-8 text-blue-500 animate-spin" />
                            ) : (
                                <>
                                    <div className="p-3 bg-slate-50 text-slate-400 rounded-full mb-3 group-hover:bg-blue-50 group-hover:text-blue-500 transition-colors">
                                        <Upload className="h-6 w-6" />
                                    </div>
                                    <span className="text-sm text-slate-500 group-hover:text-blue-600 font-medium">Subir Logo</span>
                                    <span className="text-[10px] text-slate-400 mt-1">PNG, JPG o SVG (Máx 2MB)</span>
                                </>
                            )}
                            <input type="file" className="hidden" accept="image/*" onChange={handleLogoUpload} />
                          </label>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="p-4 bg-slate-900 rounded-2xl border border-slate-800">
                    <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-xl bg-white/10 flex items-center justify-center">
                            <Palette className="h-6 w-6 text-blue-400" />
                        </div>
                        <div className="flex-1">
                            <h4 className="text-white text-sm font-medium">Vista Previa de Interfaz</h4>
                            <div className="flex gap-2 mt-2">
                                <Button size="sm" style={{ backgroundColor: formData.primary_color }} className="h-8 text-[10px]">Botón Primario</Button>
                                <Button size="sm" style={{ backgroundColor: formData.secondary_color }} className="h-8 text-[10px]">Botón Secundario</Button>
                            </div>
                        </div>
                    </div>
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="space-y-6">

            <Card className="border-blue-100 bg-blue-50/30">
                <CardContent className="pt-6">
                    <div className="flex items-start gap-3">
                        <div className="p-2 bg-blue-100 text-blue-600 rounded-lg">
                            <Loader2 className="h-4 w-4" />
                        </div>
                        <div>
                            <h4 className="text-sm font-semibold text-blue-900">¿Necesitas ayuda?</h4>
                            <p className="text-xs text-blue-700 mt-1 leading-relaxed">
                                Si tienes problemas para configurar tu identidad visual, contacta a soporte para asistencia técnica.
                            </p>
                        </div>
                    </div>
                </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}
